import React, { useMemo } from "react";
import styled from "styled-components";
import { UI_CAPTION_DF, UI_TAG, colors2 } from "oolib";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";

/**
 * The evidence strip under a topic answer: the computed facts the answer was
 * written from (getTopic `facts`, persisted as message.metadata.topicFacts).
 * The prose above it reads the numbers; this is where the numbers live, the
 * same split the Topics tab makes between the bar and its label. Rendered
 * from server-computed values, so it always matches the tab.
 */

const Strip = styled.div`
  background: #f3f3f3;
  border-radius: 6px;
  padding: 0.8rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Line = styled.div`
  color: ${colors2.grey60};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  align-items: center;
`;

const Cat = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const Dot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const TopicFactsStrip = ({ facts }) => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const theme = useMemo(() => buildSignalCategoryTheme(platformConfigs?.SIGNAL_CATEGORIES), [platformConfigs?.SIGNAL_CATEGORIES]);
  if (!facts?.title) return null;

  const datasetName = (ct) => __GetContentTypeConfigNew(ct)?.general?.content?.title || ct;
  const scope = facts.period
    ? `${facts.scopeMatched} of ${facts.total} observations in ${facts.period.display || facts.period.value}`
    : `${facts.total} observation${facts.total === 1 ? "" : "s"}${facts.parked ? ` · ${facts.parked} parked` : ""}`;
  const datasets = (facts.datasets || []).map((d) => `${d.count} ${datasetName(d.dataset)}`).join(" · ");

  return (
    <Strip>
      <UI_TAG style={{ color: colors2.grey60 }}>EVIDENCE · {facts.title}</UI_TAG>
      <Line>
        <UI_CAPTION_DF>
          {scope}
          {datasets ? ` · ${datasets}` : ""}
        </UI_CAPTION_DF>
      </Line>
      {facts.byCategory?.length > 0 && (
        <Row>
          {facts.byCategory.map((c) => (
            <Cat key={c.category}>
              <Dot $color={theme.fillOf(c.category)} />
              <UI_CAPTION_DF style={{ color: colors2.grey80 }}>
                {c.display} {c.share}%
              </UI_CAPTION_DF>
            </Cat>
          ))}
        </Row>
      )}
      {facts.topSubTopics?.length > 0 && (
        <Line>
          <UI_CAPTION_DF>Sub-topics: {facts.topSubTopics.map((s) => `${s.title} ${s.count}`).join(" · ")}</UI_CAPTION_DF>
        </Line>
      )}
      {facts.trend && (
        <Line>
          <UI_CAPTION_DF>
            Trend:{" "}
            {facts.trend.applicable
              ? facts.trend.shareSaturated
                ? `share cannot move (all one category); ${facts.trend.volume?.nowTotal} observations now vs ${facts.trend.volume?.prevTotal} in ${facts.trend.prevLabel}`
                : `${facts.trend.text}${facts.trend.detail ? ` — ${facts.trend.detail}` : ""}`
              : `not applicable — ${facts.trend.reason}`}
          </UI_CAPTION_DF>
        </Line>
      )}
      {facts.priorities?.length > 0 && (
        <Line>
          <UI_CAPTION_DF>Priorities: {facts.priorities.map((p, i) => `${i + 1}. ${p.title}${p.zone ? ` (${p.zone})` : ""}`).join(" · ")}</UI_CAPTION_DF>
        </Line>
      )}
      {facts.compare && (
        <Line>
          <UI_CAPTION_DF>
            Compared with {facts.compare.title} · {facts.compare.total} observations
          </UI_CAPTION_DF>
        </Line>
      )}
    </Strip>
  );
};
